import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, MessageSquareText, TriangleAlert } from "lucide-react";

import SedecShell from "@/components/layout/SedecShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  ApiError,
  basculerSmsAnnonces,
  basculerSmsAnnoncesEcole,
  getReglageSmsAnnonces,
  type ReglageSmsAnnonces,
} from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import { formatDateTime } from "@/lib/format";

/**
 * Diffusion : SMS des annonces aux familles (22/10/2026, décision du PO).
 *
 * Le SEDEC et l'administration allument ou coupent l'envoi par SMS des
 * annonces publiées par les écoles, à tout moment, sans redéploiement. Le
 * réglage est relu avant chaque SMS : couper arrête un envoi en cours. Chaque
 * bascule est écrite au journal d'audit.
 */
export const QUERY_KEY = ["sedec", "diffusion", "sms-annonces"] as const;

export const RAPPEL_COUT =
  "Les SMS sont payants : chaque annonce publiée envoie un SMS à chaque famille de la cible qui a un téléphone. Les notifications dans l'application et les courriels restent gratuits.";

export function dernierChangement(r: ReglageSmsAnnonces): string {
  if (!r.modifieLe) return "Jamais modifié.";
  return `Dernier changement le ${formatDateTime(r.modifieLe)}${r.modifiePar ? `, par ${r.modifiePar}` : ""}.`;
}

export default function SedecDiffusion() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => getReglageSmsAnnonces(),
    retry: false,
  });

  const reseau = useMutation({
    mutationFn: (actif: boolean) => basculerSmsAnnonces(actif),
    onSuccess: (r) => {
      qc.setQueryData(QUERY_KEY, r);
      toast.success(r.actifReseau ? "SMS des annonces allumés." : "SMS des annonces coupés.");
    },
    onError: (e: unknown) => toast.error(friendlyError(e)),
  });

  const ecole = useMutation({
    mutationFn: (etablissementId: string) => basculerSmsAnnoncesEcole(etablissementId, true),
    onSuccess: (r) => {
      qc.setQueryData(QUERY_KEY, r);
      toast.success("L'école suit de nouveau le réglage du réseau.");
    },
    onError: (e: unknown) => toast.error(friendlyError(e)),
  });

  const refuse = error instanceof ApiError && error.status === 403;
  const coupees = (data?.ecoles ?? []).filter((e) => !e.actif);

  return (
    <SedecShell>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Diffusion</h1>
          <p className="text-muted-foreground mt-1">
            Ce qui part vers les familles quand une école publie une annonce.
          </p>
        </div>

        {isLoading && (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <Card className="p-4 text-sm text-red-700 bg-red-50 border-red-200" role="alert">
            {refuse
              ? "Réservé au SEDEC et à l'administration du réseau."
              : friendlyError(error)}
          </Card>
        )}

        {data && (
          <Card className="p-5 space-y-4" data-testid="carte-sms-annonces">
            <div className="flex items-start gap-3">
              <span className="rounded p-2 bg-slate-100 text-slate-700">
                <MessageSquareText className="h-5 w-5" />
              </span>
              <div className="flex-1">
                <label htmlFor="sms-annonces" className="font-medium">SMS des annonces aux familles</label>
                <p className="text-sm text-muted-foreground mt-1" data-testid="etat-sms">
                  {data.actifReseau ? "Allumé" : "Coupé"} pour le réseau.{" "}
                  {data.effectif
                    ? "Les annonces partent aussi par SMS."
                    : "Aucun SMS d'annonce ne part."}
                </p>
                <p className="text-xs text-muted-foreground mt-1" data-testid="dernier-changement">
                  {dernierChangement(data)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {reseau.isPending && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                <Switch
                  id="sms-annonces"
                  checked={data.actifReseau}
                  disabled={reseau.isPending}
                  onCheckedChange={(v) => reseau.mutate(v)}
                  aria-label="SMS des annonces aux familles"
                />
              </div>
            </div>

            <p className="text-sm rounded bg-amber-50 text-amber-900 p-3" data-testid="rappel-cout">
              {RAPPEL_COUT}
            </p>

            {!data.plafondEnvironnement && (
              <p className="text-sm rounded bg-red-50 text-red-700 p-3 flex gap-2" data-testid="plafond-coupe">
                <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
                La configuration du serveur bloque tous les SMS d'annonces : l'interrupteur n'aura
                d'effet qu'une fois ce blocage levé par l'équipe technique.
              </p>
            )}

            {coupees.length > 0 && (
              <div>
                <p className="text-sm font-medium">Écoles où les SMS sont coupés</p>
                <ul className="mt-2 divide-y border rounded">
                  {coupees.map((e) => (
                    <li key={e.etablissementId} className="flex items-center justify-between gap-3 p-3 text-sm">
                      <span>
                        {e.nom ?? e.etablissementId}
                        <span className="block text-xs text-muted-foreground">
                          {e.modifieLe ? `Depuis le ${formatDateTime(e.modifieLe)}` : ""}
                          {e.modifiePar ? `, par ${e.modifiePar}` : ""}
                        </span>
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={ecole.isPending}
                        onClick={() => ecole.mutate(e.etablissementId)}
                      >
                        Rendre au réglage du réseau
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        )}
      </div>
    </SedecShell>
  );
}
